<template>
  <div class="ism-page-wrapper">

    <!-- Card Container -->
    <div class="ism-detail-card">
      
      <!-- Skeleton -->
      <template v-if="loading">
        <div class="ism-detail-card-header" style="border-bottom: 1px solid var(--border-color);">
          <div style="display: flex; justify-content: space-between; margin-bottom: 24px;">
            <div style="display:flex;gap:20px;">
              <div class="ism-shimmer" style="width:64px;height:64px;border-radius:16px;flex-shrink:0;"></div>
              <div style="display:flex;flex-direction:column;gap:10px;justify-content:center;">
                <div class="ism-shimmer" style="width:200px;height:24px;border-radius:6px;"></div>
                <div class="ism-shimmer" style="width:140px;height:14px;border-radius:6px;"></div>
              </div>
            </div>
          </div>
          <div style="display: flex; gap: 8px;">
            <div class="ism-shimmer" style="width:100px;height:36px;border-radius:6px;"></div>
            <div class="ism-shimmer" style="width:100px;height:36px;border-radius:6px;"></div>
          </div>
        </div>
      </template>

      <!-- Loaded Header -->
      <template v-else-if="society">
        <div class="ism-detail-card-header">
          
          <div class="ism-header-top">
            <div class="ism-header-left">
              <div class="ism-soc-logo-wrap">
                <PremiumAvatar
                  :image="societyLogoUrl"
                  :name="society.name"
                  :size="72"
                  variant="squircle"
                />
              </div>
              
              <div class="ism-soc-header-info">
                <div style="display:flex;align-items:center;gap:12px;margin-bottom:6px;flex-wrap:wrap;">
                  <span class="ism-soc-name">{{ society.name || '—' }}</span>
                  <span class="ism-badge" :class="society.is_active ? 'ism-badge-green' : 'ism-badge-red'">
                    {{ society.is_active ? 'Active' : 'Inactive' }}
                  </span>
                  <span v-if="society.society_code" class="ism-soc-code"># {{ society.society_code }}</span>
                </div>
                <div class="ism-header-meta">
                  <div class="ism-meta-item">
                    <v-icon size="14" style="opacity: 0.7;">mdi-email-outline</v-icon>
                    {{ society.contact_person_email || 'N/A' }}
                  </div>
                  <span class="ism-meta-sep">|</span>
                  <div class="ism-meta-item">
                    <v-icon size="14" style="opacity: 0.7;">mdi-calendar-outline</v-icon>
                    Created {{ formatDate(society.created_at || society.createdAt) }}
                  </div>
                </div>
              </div>
            </div>

            <button class="ism-btn-cancel ism-back-btn" @click="$router.go(-1)">
              <v-icon size="15" color="var(--accent)">mdi-arrow-left</v-icon>
              Back
            </button>
          </div>

          <!-- Tab Navigation (Hidden as only one tab remains) -->
          <div v-if="tabs.length > 1" class="ism-tab-bar">
            <button
              v-for="tab in tabs"
              :key="tab.name"
              class="ism-tab-btn"
              :class="{ 'active': currentTab === tab.name }"
              @click="switchTab(tab.name)"
            >
              {{ tab.name }}
            </button>
          </div>
          
        </div>
      </template>

      <!-- Dynamic Tab Content Area -->
      <div class="ism-tabs-content-scroll">
        <component :is="currentTabComponent" :society_id="society_id" :society_prop="society" @update-society="fetchSociety" />
      </div>

    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import BasicInformation from './BasicInformation.vue';
import moment from 'moment';
import PremiumAvatar from '../../common/PremiumAvatar.vue';

export default {
  name: 'SocietyDetails',
  components: { BasicInformation, PremiumAvatar },

  data() {
    return {
      loading: true,
      society_id: '',
      society: null,
      currentTab: 'Overview',
      tabs: [
        { name: 'Overview' },
      ],
    };
  },

  computed: {
    currentTabComponent() {
      return 'BasicInformation';
    },
    societyLogoUrl() {
      if (!this.society || !this.society.logo_url) return null;
      return this.$resolveUploadUrl(this.society.logo_url);
    },
  },

  methods: {
    ...mapMutations('society', ['setSelectedTab']),
    ...mapMutations('alert', ['setAlert']),

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM, YYYY');
    },

    switchTab(name) {
      this.currentTab = name;
      this.setSelectedTab({ selectedTab: name, societyId: this.society_id });
    },

    fetchSociety() {
      this.$axios.get(`superadmin/society/${this.society_id}`).then(res => {
        if (res.data.status) {
          this.society = res.data.society;
        }
        this.loading = false;
      }).catch(() => {
        this.loading = false;
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: 'Failed to load society.' });
      });
    }
  },

  created() {
    this.society_id = this.$route.params.id;
  },

  mounted() {
    // Restore tab only if it belongs to the SAME society
    const storedTab = this.$store.state.society.selectedTab;
    const storedSocId = this.$store.state.society.selectedSocietyId;

    if (storedTab && storedSocId === this.society_id) {
      this.currentTab = storedTab === 'Basic' ? 'Overview' : storedTab;
    } else {
      // Reset to default for new society
      this.currentTab = 'Overview';
      this.setSelectedTab({ selectedTab: 'Overview', societyId: this.society_id });
    }
    
    this.fetchSociety();
  },
};
</script>

<style scoped>
.ism-page-wrapper {
  max-width: 100%;
  padding: 0;
}

/* ── Card Container ───────────────────────── */
.ism-detail-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Card Header ──────────────────────────── */
.ism-detail-card-header {
  padding: 0;
  border-bottom: 1px solid var(--border-color);
  background: var(--card-bg);
}

.ism-header-top {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 24px;
  background: linear-gradient(180deg, var(--bg-primary) 0%, var(--card-bg) 100%);
  flex-wrap: wrap;
}

.ism-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
  flex: 1;
  min-width: 0;
}

/* Avatar */
.ism-soc-logo-wrap {
  flex-shrink: 0;
}

/* Info */
.ism-soc-header-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.ism-soc-name {
  font-size: 22px; font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.02em; line-height: 1.2;
  margin: 0;
}
.ism-soc-code {
  font-size: 11.5px; font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px; padding: 2px 8px;
  font-family: "JetBrains Mono", monospace;
}

/* Meta Data */
.ism-header-meta {
  font-size: 13px; color: var(--text-secondary);
  font-weight: 500; display: flex; align-items: center;
  gap: 12px; flex-wrap: wrap; margin-top: 8px;
}
.ism-meta-item {
  display: flex; align-items: center; gap: 6px; white-space: nowrap;
}
.ism-meta-sep {
  opacity: 0.3; font-weight: 300; font-size: 14px;
}

/* ── Tab Bar ──────────────────────────────── */
.ism-tab-bar {
  display: flex;
  gap: 8px;
  padding: 0 28px;
  overflow-x: auto;
  scrollbar-width: none;
}
.ism-tab-bar::-webkit-scrollbar { display: none; }

.ism-tab-btn {
  background: none; border: none; padding: 14px 24px; font-size: 13.5px;
  font-weight: 600; color: var(--text-secondary);
  border-bottom: 3px solid transparent;
  white-space: nowrap; transition: all 0.2s ease;
  font-family: "Inter", sans-serif; cursor: pointer;
}
.ism-tab-btn:hover { color: var(--text-primary); }
.ism-tab-btn.active {
  color: var(--accent);
  border-bottom-color: var(--accent);
}

/* ── Content Scroll Area ──────────────────── */
.ism-tabs-content-scroll {
  background: var(--card-bg);
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 900px) {
  .ism-header-top { padding: 20px; flex-direction: column; align-items: flex-start; }
  .ism-header-left { width: 100%; }
  .ism-back-btn { margin-left: 0; align-self: stretch; justify-content: center; }
  .ism-tab-bar { padding: 0 20px; }
}

@media (max-width: 600px) {
  .ism-soc-name { font-size: 18px; }
  .ism-header-meta { gap: 8px; }
  .ism-meta-sep { display: none; }
  .ism-meta-item { width: 100%; }
}
</style>
